<template>
  <div class="curve-chart-root">
    <div class="asd"></div>
    <!--
      The canvas box is sized by its container, never by its own content: chart.js sets
      the canvas height to the box height, so a box sized by the canvas feeds back into
      itself and grows a step every frame after it is drawn - the "opens at one height,
      then stretches" jump. The editor sizes this box with flex-basis 0 for that reason.
    -->
    <BarChart
      :chartData="datacollection"
      :options="options"
      ref="barChart"
      css-classes="curve-chart-canvas"
      :styles="{position: 'relative', width: '100%'}"
    />
    <MessageDialog :dialog="isMessageDialogOpen" :message="message" @dialogClosed="isMessageDialogOpen=false"/>
  </div>
</template>

<script>
  import BarChart from './BarChart.js';
  import MessageDialog from '@/components/dialogs/MessageDialog.vue';
  import 'chartjs-plugin-dragdata';
  import cloneDeep from 'lodash/cloneDeep';
  import moment from 'moment';
  import Utils from '@/modules/Utils';

  /*
    The 48 half hour slots the curve is made of, starting at noon. Written the way the
    project's region writes the time - 24 hour in Israel, AM/PM where that is the
    convention - rather than always AM/PM, and read back by the curve points list.
  */
  const buildHourLabels = (timezone) => {
    const format = Utils.getTimeFormat(timezone);
    const noon = moment().startOf('day').add(12, 'hours');
    return Array.from({ length: 48 }, (unused, index) => noon.clone().add(index * 30, 'minutes').format(format));
  };

  export default {
    name: 'CurveChart',
    props: {
      preview: {
        type: Boolean,
        default: () => false
      },
      setPreview: {
        type: Function
      },
      originalTimezoneDatasets: {
        type: Array
      },
      timezone: {
        type: String,
        default: () => ''
      },
      duskDawnProperty: {
        type: Object
      },
      minDuskIndex: {
        type: Number
      },
      minDawnIndex: {
        type: Number
      },
      maxDuskIndex: {
        type: Number
      },
      maxDawnIndex: {
        type: Number
      },
      startDuskOffset: {
        type: Number
      },
      startDawnOffset: {
        type: Number
      },
      endDuskOffset: {
        type: Number
      },
      endDawnOffset: {
        type: Number
      },
      duskSteps: {
        type: Number
      },
      dawnSteps: {
        type: Number
      },
      originalStart: {
        type: String
      },
      startType: {
        type: String
      },
      endType: {
        type: String
      },
      duskPowerLevel: {
        type: Number
      },
      dawnPowerLevel: {
        type: Number
      }
    },
    components: {
      BarChart,
      MessageDialog
    },
    data() {
      const vm = this;
      return {
        isMessageDialogOpen: false,
        message: '',
        dragPrevFluctuations: -1,
        previousDragDimming: 0,
        currentFluctuations: -1,
        onClickDimming: -1,
        duskDawnWindowColor: '#DCDCDC',
        duskDawnChartColor: '#FFA07A',
        defaultChartColor: '#ffeb3b',
        borderColor: '#FFFFFF',
        datacollection: {
          labels: buildHourLabels(this.timezone)
        },
        options: {
          showLines: true,
          animation: false,
          dragData: true,
          scales: {
            xAxes: [{
              ticks: {
                autoSkip: false,
                minRotation: 50,
                fontSize: 11
              }
            }],
            yAxes: [
              {
                ticks: {
                  min: 0,
                  max: 100,
                  stepSize: 10
                },
                scaleLabel: {
                  display: true,
                  labelString: this.$t('Dim Level')
                }
              }
            ]
          },
          offset: true,
          layout: {
            padding: 5
          },
          responsive: true,
          maintainAspectRatio: false,
          responsiveAnimationDuration: 0,
          legend: {
            display: false
          },
          tooltips: {
            callbacks: {
              label: (tooltipItem) => {
                return vm.duskDawnProperty
                  ? vm.startType === 'dusk' && tooltipItem.index >= vm.startDuskOffset && tooltipItem.index < vm.endDuskOffset 
                  ? ` Dusk Power: ${vm.duskPowerLevel}`
                  : vm.endType === 'dawn' && tooltipItem.index >= vm.startDawnOffset && tooltipItem.index < vm.endDawnOffset
                  ? ` Dawn Power: ${vm.dawnPowerLevel}`
                  : tooltipItem.yLabel
                  : tooltipItem.yLabel;
              }
            }
          },
          dragDataRound: 0,
          onDragStart(e, element) {
            vm.onClickDimming = -1;
            vm.currentFluctuations = -1;
            if (vm.startType === 'dusk' && vm.duskDawnProperty && ((element._index >= vm.startDuskOffset && element._index < vm.endDuskOffset) ||
                (element._index >= vm.startDawnOffset && element._index < vm.endDawnOffset))) {
              return false;
            }
            // Taken only for a drag that actually starts: a cancelled one never reaches
            // onDragEnd to clear it, and the next click would compare against a stale count.
            vm.previousDragDimming = vm.datacollection.datasets[0].data[element._index];
            vm.dragPrevFluctuations = vm.checkNumOfCurves(vm.datacollection.datasets[0].data);
          },
          onDragEnd(e, datasetIndex, index, value) {
            setTimeout(() => {
              const current_fluctuations = vm.currentFluctuations > -1 
                ? vm.currentFluctuations 
                : vm.checkNumOfCurves(vm.datacollection.datasets[0].data);
              // Every value is checked, 0 included: a 0 set inside a lit stretch splits it
              // into two fluctuations.
              if (current_fluctuations > 8 && vm.dragPrevFluctuations < current_fluctuations){
                vm.datacollection.datasets[0].data[index] = vm.previousDragDimming;
                vm.message = 'You have reached the maximum number of dimming fluctuations in the curve, to add a fluctuation you must delete one';
                vm.isMessageDialogOpen = true;
                vm.$refs.barChart.renderChart(vm.datacollection, vm.options);
                vm.dragPrevFluctuations = -1;
                return false;
              }
              vm.dragPrevFluctuations = -1;
              vm.emitDatasets();
            }, 100);
          },
          onClick(event) {
            let index = null, valueY = null;
            for (const scaleName in this.scales) {
              const scale = this.scales[scaleName];
              if (scale.isHorizontal()) {
                index = scale.getValueForPixel(event.offsetX);
                if (vm.duskDawnProperty){
                  if ((vm.startType === 'dusk' && index >= vm.startDuskOffset && index < vm.endDuskOffset) ||
                    (vm.endType === 'dawn' && index >= vm.startDawnOffset && index < vm.endDawnOffset)) {
                      vm.message = 'You can control dimming level of dusk and dawn in power field';
                      vm.isMessageDialogOpen = true;
                      return false;
                  }
                }
              } else {
                const tempValue = scale.getValueForPixel(event.offsetY);
                valueY = tempValue < 0 ? 0 : tempValue > 100 ? 100 : tempValue;
              }
            }
            if (index >= 0){
              const current_data = cloneDeep(vm.datacollection.datasets[0].data);
              vm.onClickDimming = Math.round(valueY);
              current_data[index] = vm.onClickDimming;
              vm.currentFluctuations = vm.checkNumOfCurves(current_data);
              const previous_fluctuations = vm.dragPrevFluctuations > -1 ? vm.dragPrevFluctuations : vm.checkNumOfCurves(vm.datacollection.datasets[0].data);
              if (vm.currentFluctuations > 8 && previous_fluctuations < vm.currentFluctuations){
                vm.message = 'You have reached the maximum number of dimming fluctuations in the curve, to add a fluctuation you must delete one';
                vm.isMessageDialogOpen = true;
                return false;
              }else vm.datacollection.datasets[0].data[index] = current_data[index];
            }
            this.update();
            vm.emitDatasets();
          }
        }
      };
    },
    mounted() {
      this.chartIntialization();
      this.observeChartBox();
      this.$nextTick(() => {
        if (this.preview && this.$refs.barChart) {
          const image = this.$refs.barChart.$refs.canvas.toDataURL('image/png');
          this.setPreview(image);
        }
      });
    },
    beforeDestroy() {
      if (this.box_observer) this.box_observer.disconnect();
    },
    methods: {
      chartIntialization(){
        const color = this.startType === 'dusk' && this.duskDawnProperty ? this.duskDawnChartColor : this.defaultChartColor;
        this.datacollection.datasets = [{
          data: [...this.originalTimezoneDatasets],
          backgroundColor: Array(48).fill(color),
          borderColor: Array(48).fill(this.borderColor),
          borderWidth: 0.2,
          categoryPercentage: 1.0,
          barPercentage: 1.0
        }];

        if (this.startType === 'dusk' && this.duskDawnProperty){
          this.addDuskDawnWindow();
        }
        this.$refs.barChart.renderChart(this.datacollection, this.options);
        this.emitDatasets();
      },
      onStartTypeChanged() {
        if (this.duskDawnProperty && this.startType === 'dusk'){
          this.fillDuskDawnChart();
          this.addDuskDawnWindow();
        }else this.fillChartWithOriginalPowers(0, 48, this.defaultChartColor, this.originalTimezoneDatasets, false);
        this.$refs.barChart.renderChart(this.datacollection, this.options);
        this.emitDatasets();
      },
      addDuskDawnWindow(){
        this.fillChartWithOriginalPowers(this.startDuskOffset, this.endDuskOffset, this.duskDawnWindowColor, [this.duskPowerLevel], true);
        this.fillChartWithOriginalPowers(this.startDawnOffset, this.endDawnOffset, this.duskDawnWindowColor, [this.dawnPowerLevel], true);
      },
      fillChartWithOriginalPowers(start, end, color, powers, singleValue){
        if (start > end){
          for (let i = start; i < 48; i++) {
            this.datacollection.datasets[0].data[i] = singleValue ? powers[0] : powers[i];
            this.datacollection.datasets[0].backgroundColor[i] = color;
          }
          for (let i = 0; i < end; i++) {
            this.datacollection.datasets[0].data[i] = singleValue ? powers[0] : powers[i];
            this.datacollection.datasets[0].backgroundColor[i] = color;
          }
        }else{
          for (let i = start; i < end; i++) {
            this.datacollection.datasets[0].data[i] = singleValue ? powers[0] : powers[i];
            this.datacollection.datasets[0].backgroundColor[i] = color;
          }
        }
      },
      fillChartWithRoundedPowers(start, end, color, powers){
        if (start > end){
          for (let i = start; i < 48; i++) {
            this.datacollection.datasets[0].data[i] = powers[i] === 0 ? 100 : powers[i];
            this.datacollection.datasets[0].backgroundColor[i] = color;
          }
          for (let i = 0; i < end; i++) {
            this.datacollection.datasets[0].data[i] = powers[i] === 0 ? 100 : powers[i];
            this.datacollection.datasets[0].backgroundColor[i] = color;
          }
        }else{
          for (let i = start; i < end; i++) {
            this.datacollection.datasets[0].data[i] = powers[i] === 0 ? 100 : powers[i];
            this.datacollection.datasets[0].backgroundColor[i] = color;
          }
        }
      },
      fillDuskDawnChart(){
        this.fillChartWithRoundedPowers(this.endDuskOffset, this.startDawnOffset, this.duskDawnChartColor, this.originalTimezoneDatasets);
        this.fillChartWithOriginalPowers(this.endDawnOffset, this.startDuskOffset, this.duskDawnChartColor, this.originalTimezoneDatasets, false);
      },
      UpdateMinMaxDuskIndex(){
        this.fillDuskDawnChart();
        this.fillChartWithOriginalPowers(this.startDuskOffset, this.endDuskOffset, this.duskDawnWindowColor, [this.duskPowerLevel], true);
        this.$refs.barChart.renderChart(this.datacollection, this.options);
        this.emitDatasets();
      },
      UpdateMinMaxDawnIndex(){
        this.fillDuskDawnChart();
        this.fillChartWithOriginalPowers(this.startDawnOffset, this.endDawnOffset, this.duskDawnWindowColor, [this.dawnPowerLevel], true);
        this.$refs.barChart.renderChart(this.datacollection, this.options);
        this.emitDatasets();
      },
      getDatasets(){
        return this.datacollection.datasets[0].data;
      },
      /*
        Chart.js measures its box when it draws and afterwards only notices a change
        through a detector of its own that runs well behind it - which is what read as the
        chart appearing at one height and stretching a moment after the dialog opened.
        Watching the box instead means the canvas is resized in the frame the box changes,
        whatever changed it: the dialog settling as it opens, the icon fonts arriving, the
        settings beside it growing a row, the window being resized.
      */
      observeChartBox(){
        if (typeof ResizeObserver === 'undefined') return;
        this.box_observer = new ResizeObserver(() => this.resizeChart());
        this.box_observer.observe(this.$el);
      },
      resizeChart(){
        // The chart is rebuilt on every change to the curve, so it is looked up each time.
        const chart = this.$refs.barChart && this.$refs.barChart.$data._chart;
        if (chart) chart.resize();
      },
      /*
        The chart owns the curve values while the editor is open - they only reach the
        curve object on save. The points summary beside it needs to follow along, so
        every path that changes a bar reports the new values out.
      */
      emitDatasets(){
        if (this.preview) return;
        this.$emit('datasetsChanged', {
          data: [...this.datacollection.datasets[0].data],
          labels: [...this.datacollection.labels]
        });
      },
      showDuskDawnWarning(){
        this.$notify({
          type: 'warning',
          title: 'Warning!',
          text: 'There is no dusk dawn property defined for this project. Dusk Dawn will not work.'
        });
      },
      /*
        The number of rows the curve takes in the device memory: only a dim level above 0
        is written, one row for every stretch of consecutive slots on the same level, so
        50, 0, 50 is two and a change from 100 straight to 70 is a row of its own.
      */
      checkNumOfCurves(dataset_data){
        return dataset_data.filter((dimming_level, index) =>
          dimming_level > 0 && (index === 0 || dimming_level !== dataset_data[index - 1])
        ).length;
      }
    },
    watch: {
      startType() {
        if ((this.startType === 'dusk' && this.endType !== 'dawn') || (this.startType === 'none' && this.endType !== 'none')){          
          this.onStartTypeChanged();
          const endType = this.startType === 'dusk' ? 'dawn' : 'none';
          this.$emit('onStartTypeChanged', endType);
        }
        if (!this.duskDawnProperty && this.startType === 'dusk' && this.endType !== 'dawn') this.showDuskDawnWarning();
      },
      endType() {
        if ((this.endType === 'dawn' && this.startType !== 'dusk') || (this.endType === 'none' && this.startType !== 'none')){
          const startType = this.endType === 'dawn' ? 'dusk' : 'none';
          this.$emit('onEndTypeChanged', startType);
          this.$nextTick(() => this.onStartTypeChanged());
        }
        if (!this.duskDawnProperty && this.endType === 'dawn' && this.startType !== 'dusk') this.showDuskDawnWarning();
      },
      timezone(){
        // The zone decides how the hours are written, so the labels follow it.
        this.datacollection.labels = buildHourLabels(this.timezone);
        this.onStartTypeChanged();
      },
      duskPowerLevel(){
        if (this.duskDawnProperty && this.startType === 'dusk'){
          this.fillChartWithOriginalPowers(this.startDuskOffset, this.endDuskOffset, this.duskDawnWindowColor, [this.duskPowerLevel], true);
          this.$refs.barChart.renderChart(this.datacollection, this.options);
          // The window's own dim level is part of the curve, so the points list has to
          // hear about it - it becomes a point of its own whenever it differs.
          this.emitDatasets();
        }
      },
      dawnPowerLevel(){
        if (this.duskDawnProperty && this.startType === 'dusk'){
          this.fillChartWithOriginalPowers(this.startDawnOffset, this.endDawnOffset, this.duskDawnWindowColor, [this.dawnPowerLevel], true);
          this.$refs.barChart.renderChart(this.datacollection, this.options);
          this.emitDatasets();
        }
      },
      endDuskOffset(){
        if (this.duskDawnProperty && this.startType === 'dusk') this.UpdateMinMaxDuskIndex();
      },
      endDawnOffset(){
        if (this.duskDawnProperty && this.startType === 'dusk') this.UpdateMinMaxDawnIndex();
      }
    }
  };

</script>

<style>
  /*
    The height the chart keeps on its own (preview cards, standalone use): a fixed
    height cannot feed back into itself, so the chart is steady from the first frame.
    In the curve editor the .curve-chart-fill class overrides this with a flex-basis-0
    share of the editor's column - also container-driven, also stable.
  */
  .curve-chart-root {
    display: flex;
    flex-direction: column;
    height: 500px;
  }

  .curve-chart-root > .curve-chart-canvas {
    flex: 1 1 auto;
    min-height: 0;
  }

  .small {
    max-width: 1000px;
    margin: 0 auto;
  }

  #bar-chart:hover {
    cursor: pointer;
  }
  .dialog-class {
    border-radius: 20px;
  }
</style>
